import { useCallback } from 'react';
import { SelectItem } from '@/types/ui';
import { createStableObjectHash } from '@/storybook/components/FormCollection/utils/utils';

const getValueKey = (valueToCheck: unknown) =>
  valueToCheck !== null && typeof valueToCheck === 'object'
    ? createStableObjectHash(valueToCheck)
    : String(valueToCheck);

type UseMultiLevelSelectHandlersProps<T> = {
  value: SelectItem<T>[];
  onChange: (selectedValue: SelectItem<T>[]) => void;
  disabled?: boolean;
  clearable?: boolean;
  setSelectedOption: (option: SelectItem<T> | undefined) => void;
};

export const useMultiLevelSelectHandlersFloatingLabel = <T>({
  value,
  onChange,
  disabled,
  clearable,
  setSelectedOption,
}: UseMultiLevelSelectHandlersProps<T>) => {
  const handleClearValue = useCallback(() => {
    if (disabled || !clearable) return;
    onChange([]);
  }, [disabled, clearable, onChange]);

  const handleClickOnCheckbox = useCallback(
    (option: SelectItem<T>) => {
      if (disabled) return;

      const isAlreadyInValue = value.find(
        existingItem => getValueKey(existingItem.value) === getValueKey(option.value),
      );
      if (isAlreadyInValue) {
        onChange(value.filter(existingItem => getValueKey(existingItem.value) !== getValueKey(option.value)));
      } else {
        setSelectedOption(option);
      }
    },
    [value, onChange, disabled, setSelectedOption],
  );

  const handleSelectLevel = useCallback(
    (selectedOption: SelectItem<T>, level: number) => {
      const updatedValue = [
        ...value.filter(existingItem => getValueKey(existingItem.value) !== getValueKey(selectedOption.value)),
      ];
      updatedValue.push({ ...selectedOption, level });
      onChange(updatedValue);
      setSelectedOption(undefined);
    },
    [value, onChange, setSelectedOption],
  );

  return {
    handleClearValue,
    handleClickOnCheckbox,
    handleSelectLevel,
  };
};
